import { useState } from "react";
import {
  useProductBase,
  useProductImages,
  useProductPriceInfo,
  useProductDataModel,
  ProductLink,
} from "@made-people/grease-product";
import { useUpdateCart } from "@made-people/grease-cart";
import { useTranslate } from "@made-people/grease-translations";
import { ResponsiveImage } from "@made-people/blueprint-responsive-image";
import { QuickshopSelector } from "@made-people/blueprint-product-card";

interface MMUpsellProductCardProps {
  context: "minicart" | "checkout";
}

export const MMUpsellProductCard = ({ context }: MMUpsellProductCardProps) => {
  const productBase = useProductBase();
  const images = useProductImages();
  const priceInfo = useProductPriceInfo();
  const dataModel = useProductDataModel();

  const firstImage = images?.[0];
  const isDiscounted =
    priceInfo.price.formatted !== priceInfo.priceWithoutDiscount.formatted;
  const hasVariants = dataModel === "with_variants";

  return (
    <div className="flex h-auto min-h-36 items-start gap-4 overflow-hidden rounded bg-white p-4">
      <ProductLink
        productBase={productBase}
        className="relative h-28 w-[84px] shrink-0"
      >
        {firstImage && (
          <ResponsiveImage
            className="absolute inset-0 h-full w-full object-cover"
            initial={{
              src: firstImage.src,
              alt: firstImage.alt || productBase.content.name,
            }}
            displaySize={{ mobile: "84px", desktop: "84px" }}
          />
        )}
      </ProductLink>

      <div className="flex min-w-0 flex-1 flex-col justify-between self-stretch">
        <div className="flex flex-col">
          <ProductLink
            productBase={productBase}
            className="text-body-md font-medium leading-snug line-clamp-2"
          >
            {productBase.content.name}
          </ProductLink>
          <div className="flex items-center gap-1.5 text-body-md font-medium">
            <span>{priceInfo.price.formatted}</span>
            {isDiscounted && (
              <span className="text-sale-price line-through">
                {priceInfo.priceWithoutDiscount.formatted}
              </span>
            )}
          </div>
        </div>

        {hasVariants ? (
          <UpsellQuickshop context={context} />
        ) : (
          <UpsellSimpleButton />
        )}
      </div>
    </div>
  );
};

const UpsellSimpleButton = () => {
  const translate = useTranslate();
  const [added, setAdded] = useState(false);

  const { addToCartMethod } = useUpdateCart({
    success: () => {
      setAdded(true);
      setTimeout(() => setAdded(false), 2000);
    },
  });

  const handleAdd = async () => {
    if (addToCartMethod.loadingState === "loading") return;
    await addToCartMethod.execute();
  };

  return (
    <button
      onClick={handleAdd}
      disabled={addToCartMethod.loadingState === "loading"}
      className="flex h-10 w-full items-center justify-center rounded bg-button-primary-default-bg text-button-md text-button-primary-default-label transition-colors hover:bg-button-primary-hover-bg disabled:opacity-50"
    >
      {added ? (
        <span className="flex items-center gap-2">
          {translate("Added")}
          <CheckIcon />
        </span>
      ) : addToCartMethod.loadingState === "loading" ? (
        <LoadingSpinner />
      ) : (
        translate("Add")
      )}
    </button>
  );
};

const UPSELL_QUICKSHOP_OVERRIDES =
  "[&_[data-is-one-size]]:!static [&_[data-is-one-size]]:!inset-auto [&_[data-is-one-size]]:!z-auto " +
  "[&_[data-is-one-size]]:!w-full [&_[data-is-one-size]]:!h-10 [&_[data-is-one-size]]:!rounded [&_[data-is-one-size]]:!p-0 " +
  "[&_[data-is-one-size]]:!bg-button-primary-default-bg [&_[data-is-one-size]]:!text-button-primary-default-label [&_[data-is-one-size]]:!text-button-md " +
  "[&_[data-is-one-size]]:!flex [&_[data-is-one-size]]:!items-center [&_[data-is-one-size]]:!justify-center " +
  "[&_[data-is-one-size]]:hover:!bg-button-primary-hover-bg [&_[data-is-one-size]]:!transition-colors";

const UpsellQuickshop = ({ context }: { context: "minicart" | "checkout" }) => {
  const t = useTranslate();

  return (
    <div className={UPSELL_QUICKSHOP_OVERRIDES}>
      <QuickshopSelector
        configs={[
          {
            config: {
              sizesCount: { from: 0, to: 25 },
              optionsCount: { from: 0, to: 10 },
            },
            template: {
              template: "drawer",
              postAddAction:
                context === "minicart" ? "closeDrawer" : "showMinicart",
              drawerSettings: {
                nested: false,
              },
              trigger: (
                <span className="text-button-md text-button-primary-default-label">
                  {t("Add")}
                </span>
              ),
            },
          },
        ]}
      />
    </div>
  );
};

const CheckIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M3 8L6.5 11.5L13 5"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const LoadingSpinner = () => (
  <svg
    className="h-5 w-5 animate-spin"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <circle
      className="opacity-25"
      cx="12"
      cy="12"
      r="10"
      stroke="currentColor"
      strokeWidth="4"
    />
    <path
      className="opacity-75"
      fill="currentColor"
      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
    />
  </svg>
);
